<template>
  <div>
    <NavBar />
    <div class="page">
      <header class="stats-hero">
        <div>
          <p class="eyebrow">Performance commerciale</p>
          <h2 class="page-title">Statistiques</h2>
          <p class="page-subtitle">Analyse du chiffre d'affaires, des clients fidélisés et des tendances par marché.</p>
        </div>
        <div class="filtres-card">
          <span class="filtres-label">Période</span>
          <div class="filtres-row">
            <button class="periode-btn" :class="{ active: periode === 7 }" @click="setPeriode(7)">7 jours</button>
            <button class="periode-btn" :class="{ active: periode === 30 }" @click="setPeriode(30)">30 jours</button>
            <button class="periode-btn" :class="{ active: periode === 90 }" @click="setPeriode(90)">90 jours</button>
          </div>
        </div>
      </header>

      <div v-if="loading" class="card loading-card">
        <div class="loading-pulse"></div>
        <strong>Chargement des statistiques</strong>
        <span>Consolidation des indicateurs commerciaux...</span>
      </div>

      <template v-else>

        <!-- KPIs globaux -->
        <div class="kpis">
          <div class="kpi-card card kpi-primary">
            <div class="kpi-icon">€</div>
            <div class="kpi-value">{{ formatEuro(totalCA) }}</div>
            <div class="kpi-label">CA total</div>
          </div>
          <div class="kpi-card card">
            <div class="kpi-icon">F</div>
            <div class="kpi-value">{{ formatEuro(totalCAFidelise) }}</div>
            <div class="kpi-label">CA fidélisé</div>
          </div>
          <div class="kpi-card card">
            <div class="kpi-icon">S</div>
            <div class="kpi-value">{{ formatEuro(totalCASansCarte) }}</div>
            <div class="kpi-label">CA sans carte</div>
          </div>
          <div class="kpi-card card">
            <div class="kpi-icon">P</div>
            <div class="kpi-value">{{ totalPassages }}</div>
            <div class="kpi-label">Passages carte</div>
          </div>
          <div class="kpi-card card">
            <div class="kpi-icon">M</div>
            <div class="kpi-value">{{ formatEuro(panierMoyen) }}</div>
            <div class="kpi-label">Panier moyen</div>
          </div>
          <div class="kpi-card card">
            <div class="kpi-icon success">+</div>
            <div class="kpi-value">{{ totalNouveauxClients }}</div>
            <div class="kpi-label">Nouveaux clients</div>
          </div>
        </div>

        <!-- CA par jour -->
        <div class="card chart-card">
          <div class="chart-header">
            <h3>📅 CA par jour</h3>
            <div class="chart-style-toggle">
              <button :class="{ active: styleGraphique === 'barres' }" @click="setStyle('barres')" title="Barres">▊</button>
              <button :class="{ active: styleGraphique === 'courbe' }" @click="setStyle('courbe')" title="Courbe">〜</button>
            </div>
          </div>
          <div class="chart-wrap" v-if="parJour.length">
            <!-- Mode barres -->
            <div class="barres-chart" v-if="styleGraphique === 'barres'">
              <div class="barre-item" v-for="j in parJourFiltre" :key="j.jour">
                <div class="barre-top-label">{{ formatEuroShort(j.ca_total) }}</div>
                <div class="barre-outer">
                  <div class="barre-inner b-ca" :style="{ height: (j.ca_total / maxJour * 100) + '%' }"></div>
                </div>
                <div class="barre-bot-label">{{ formatJour(j.jour) }}</div>
              </div>
            </div>
            <!-- Mode courbe SVG -->
            <div class="courbe-chart" v-else>
              <svg viewBox="0 0 700 210" preserveAspectRatio="xMidYMid meet" class="courbe-svg">
                <defs>
                  <linearGradient id="gradca" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#1E6FDC" stop-opacity="0.18"/>
                    <stop offset="100%" stop-color="#1E6FDC" stop-opacity="0"/>
                  </linearGradient>
                </defs>
                <!-- Grille horizontale -->
                <line v-for="n in 4" :key="'g'+n" x1="44" x2="686" :y1="20 + (n-1) * 34" :y2="20 + (n-1) * 34" stroke="#E2E8F0" stroke-width="1"/>
                <!-- Labels Y -->
                <text v-for="n in 4" :key="'gy'+n" x="40" :y="24 + (n-1) * 34" text-anchor="end" font-size="9" fill="#64748B" font-family="sans-serif">{{ formatEuroShort(maxJour * (4-n) / 3) }}</text>
                <!-- Zone remplie -->
                <path :d="courbeArea" fill="url(#gradca)"/>
                <!-- Ligne -->
                <path :d="courbeLine" fill="none" stroke="#1E6FDC" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
                <!-- Points + labels -->
                <g v-for="(j, i) in parJourFiltre" :key="j.jour">
                  <text :x="courbeX(i)" :y="courbeY(j.ca_total) - 9" text-anchor="middle" font-size="9" font-weight="700" fill="#0D1B3D" font-family="sans-serif">{{ formatEuroShort(j.ca_total) }}</text>
                  <circle :cx="courbeX(i)" :cy="courbeY(j.ca_total)" r="5" fill="white" stroke="#1E6FDC" stroke-width="2.5"/>
                  <text :x="courbeX(i)" y="202" text-anchor="middle" font-size="9" fill="#64748B" font-family="sans-serif">{{ formatJour(j.jour) }}</text>
                </g>
              </svg>
            </div>
          </div>
          <div class="vide" v-else>Aucune donnée</div>
        </div>

        <!-- CA par marché -->
        <div class="card">
          <h3>📍 CA par marché</h3>
          <div class="marche-stats" v-if="parVille.length">
            <div class="marche-row" v-for="v in parVille" :key="v.ville">
              <div class="marche-info">
                <span class="marche-nom">📍 {{ v.ville }}</span>
                <span class="marche-detail">{{ v.nb_journees }} journées · {{ v.passages }} passages</span>
              </div>
              <div class="marche-bar-wrap">
                <div class="marche-bar" :style="{ width: (v.ca_total / maxVille * 100) + '%' }"></div>
              </div>
              <div class="marche-ca">{{ formatEuro(v.ca_total) }}</div>
            </div>
          </div>
          <div class="vide" v-else>Aucune donnée</div>
        </div>

        <!-- Meilleur jour de la semaine -->
        <div class="card">
          <div class="chart-header">
            <h3>📆 CA moyen par jour de semaine</h3>
            <div class="chart-style-toggle">
              <button :class="{ active: styleGraphiqueSemaine === 'barres' }" @click="setStyleSemaine('barres')" title="Barres">▊</button>
              <button :class="{ active: styleGraphiqueSemaine === 'courbe' }" @click="setStyleSemaine('courbe')" title="Courbe">〜</button>
            </div>
          </div>
          <div v-if="parJourSemaine.length">
            <!-- Barres verticales -->
            <div class="semaine-scroll" v-if="styleGraphiqueSemaine === 'barres'">
              <div class="semaine-grid">
                <div
                  class="semaine-item"
                  v-for="j in parJourSemaine"
                  :key="j.dow"
                  :class="{ 'semaine-peak': j.ca_moyen == maxSemaine }"
                >
                  <div class="semaine-ca">{{ formatEuroShort(j.ca_moyen) }}</div>
                  <div class="semaine-bar-wrap">
                    <div class="semaine-bar" :style="{ height: (j.ca_moyen / maxSemaine * 100) + '%' }"></div>
                  </div>
                  <div class="semaine-jour">{{ nomJour(j.dow) }}</div>
                  <div class="semaine-nb">{{ j.nb_journees }} j.</div>
                </div>
              </div>
            </div>
            <!-- Courbe SVG -->
            <div class="courbe-chart" v-else>
              <svg viewBox="0 0 700 210" preserveAspectRatio="xMidYMid meet" class="courbe-svg">
                <defs>
                  <linearGradient id="gradsem" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#22C55E" stop-opacity="0.18"/>
                    <stop offset="100%" stop-color="#22C55E" stop-opacity="0"/>
                  </linearGradient>
                </defs>
                <line v-for="n in 4" :key="'gs'+n" x1="44" x2="686" :y1="20 + (n-1) * 34" :y2="20 + (n-1) * 34" stroke="#E2E8F0" stroke-width="1"/>
                <text v-for="n in 4" :key="'gsy'+n" x="40" :y="24 + (n-1) * 34" text-anchor="end" font-size="9" fill="#64748B" font-family="sans-serif">{{ formatEuroShort(maxSemaine * (4-n) / 3) }}</text>
                <path :d="courbeSemaineLine" fill="none" stroke="#22C55E" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>
                <path :d="courbeSemaineArea" fill="url(#gradsem)"/>
                <g v-for="(j, i) in parJourSemaine" :key="j.dow">
                  <text :x="courbeSemaineX(i)" :y="courbeSemaineY(j.ca_moyen) - 9" text-anchor="middle" font-size="9" font-weight="700" fill="#0D1B3D" font-family="sans-serif">{{ formatEuroShort(j.ca_moyen) }}</text>
                  <circle :cx="courbeSemaineX(i)" :cy="courbeSemaineY(j.ca_moyen)" r="5" fill="white" stroke="#22C55E" stroke-width="2.5"/>
                  <text :x="courbeSemaineX(i)" y="202" text-anchor="middle" font-size="9" fill="#64748B" font-family="sans-serif">{{ nomJour(j.dow) }}</text>
                </g>
              </svg>
            </div>
          </div>
          <div class="vide" v-else>Aucune donnée</div>
        </div>

        <!-- CB vs Espèces -->
        <div class="card paiement-card" v-if="paiements">
          <h3>💳 Répartition des paiements</h3>
          <div class="paiement-row">
            <div class="paiement-item especes">
              <div class="paiement-icon">💵</div>
              <div class="paiement-val">{{ formatEuro(paiements.total_especes || 0) }}</div>
              <div class="paiement-label">Espèces</div>
              <div class="paiement-pct">{{ pctEspeces }}%</div>
            </div>
            <div class="paiement-divider">
              <div class="paiement-barre">
                <div class="paiement-barre-especes" :style="{ width: pctEspeces + '%' }"></div>
                <div class="paiement-barre-cb" :style="{ width: pctCb + '%' }"></div>
              </div>
            </div>
            <div class="paiement-item cb">
              <div class="paiement-icon">💳</div>
              <div class="paiement-val">{{ formatEuro(paiements.total_cb || 0) }}</div>
              <div class="paiement-label">CB</div>
              <div class="paiement-pct">{{ pctCb }}%</div>
            </div>
          </div>
        </div>

        <!-- Nouveaux clients par semaine -->
        <div class="card">
          <h3>🆕 Nouveaux clients par semaine</h3>
          <div class="barres-chart" v-if="nouveauxClients.length">
            <div class="barre-item" v-for="s in nouveauxClients" :key="s.semaine">
              <div class="barre-top-label">{{ s.nb_nouveaux }}</div>
              <div class="barre-outer">
                <div class="barre-inner b-clients" :style="{ height: (s.nb_nouveaux / maxNouveaux * 100) + '%' }"></div>
              </div>
              <div class="barre-bot-label">{{ formatSemaine(s.semaine) }}</div>
            </div>
          </div>
          <div class="vide" v-else>Aucune donnée</div>
        </div>

        <!-- Top 10 clients -->
        <div class="card">
          <h3>🏆 Top 10 clients</h3>
          <div class="table-scroll" v-if="topClients.length">
            <table class="table-top">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Client</th>
                  <th>Carte</th>
                  <th>Achats</th>
                  <th>CA total</th>
                  <th>Points</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in topClients" :key="c.numero_carte" :class="{ 'top-1': i === 0, 'top-2': i === 1, 'top-3': i === 2 }">
                  <td class="rank">{{ i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1 }}</td>
                  <td data-label="Client"><strong>{{ c.prenom }} {{ c.nom }}</strong></td>
                  <td data-label="Carte" class="mono">{{ c.numero_carte }}</td>
                  <td data-label="Achats">{{ c.nb_achats }}</td>
                  <td data-label="CA total" class="ca">{{ formatEuro(c.ca_total) }}</td>
                  <td data-label="Points"><span class="badge-pts">{{ c.points_solde }} pts</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="vide" v-else>Aucune donnée</div>
        </div>


        <!-- Clients inactifs -->
        <div class="card inactifs-card">
          <div class="inactifs-header">
            <h3>😴 Clients inactifs</h3>
            <div class="inactifs-meta">
              <div class="inactifs-seuil-edit">
                <label>Seuil :</label>
                <input v-model.number="seuilInactivite" type="number" min="1" max="52" class="seuil-input" @change="sauvegarderSeuil" />
                <span>sem. sans achat</span>
              </div>
              <button class="btn-rafraichir" @click="chargerInactifs" :disabled="loadingInactifs">
                {{ loadingInactifs ? '...' : '🔄 Actualiser' }}
              </button>
            </div>
          </div>
          <div class="inactifs-kpis" v-if="clientsInactifs.length">
            <div class="inactif-kpi">
              <strong>{{ clientsInactifs.length }}</strong>
              <span>clients inactifs</span>
            </div>
            <div class="inactif-kpi">
              <strong>{{ moyenneAbsence }}</strong>
              <span>semaines moy. absence</span>
            </div>
            <div class="inactif-kpi warn">
              <strong>{{ clientsInactifs.filter(c => c.semaines_absence >= seuilInactivite * 2).length }}</strong>
              <span>perdus (2x seuil)</span>
            </div>
          </div>
          <div v-if="loadingInactifs" class="vide">Chargement...</div>
          <div v-else-if="clientsInactifs.length === 0" class="vide inactifs-vide">
            ✅ Aucun client inactif depuis {{ seuilInactivite }} semaine(s)
          </div>
          <div v-else class="inactifs-list">
            <div class="inactif-item" v-for="c in clientsInactifs" :key="c.client_id"
              :class="{ 'inactif-critique': c.semaines_absence >= seuilInactivite * 2 }">
              <div class="inactif-avatar">{{ (c.prenom || '?')[0] }}{{ (c.nom || '')[0] }}</div>
              <div class="inactif-info">
                <strong>{{ c.prenom }} {{ c.nom }}</strong>
                <span>Carte {{ c.numero_carte }} · {{ c.nb_achats }} achats · {{ formatEuro(c.ca_historique) }}</span>
              </div>
              <div class="inactif-absence">
                <strong>{{ c.semaines_absence }}sem</strong>
                <span>sans visite</span>
              </div>
              <div class="inactif-points">
                <span class="badge-pts">{{ c.points_solde }} pts</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'

export default {
  name: 'StatsView',
  components: { NavBar },
  data() {
    return {
      loading:        true,
      periode:        30,
      parJour:        [],
      parVille:       [],
      parJourSemaine: [],
      paiements:      null,
      topClients:     [],
      nouveauxClients:[],
      styleGraphique: localStorage.getItem('style_graphique') || 'barres',
      styleGraphiqueSemaine: localStorage.getItem('style_graphique_semaine') || 'barres',
      clientsInactifs:  [],
      loadingInactifs:  false,
      seuilInactivite:  parseInt(localStorage.getItem('seuil_inactivite') || '4')
    }
  },
  computed: {
    parJourFiltre() {
      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - this.periode)
      return this.parJour.filter(j => new Date(j.jour) >= cutoff)
    },
    totalCA() {
      return this.parJourFiltre.reduce((s, j) => s + parseFloat(j.ca_total || 0), 0)
    },
    totalCAFidelise() {
      return this.parJourFiltre.reduce((s, j) => s + parseFloat(j.ca_fidelise || 0), 0)
    },
    totalCASansCarte() {
      return this.parJourFiltre.reduce((s, j) => s + parseFloat(j.ca_sans_fidelite || 0), 0)
    },
    totalPassages() {
      return this.parJourFiltre.reduce((s, j) => s + parseInt(j.passages || 0), 0)
    },
    panierMoyen() {
      return this.totalPassages > 0 ? this.totalCAFidelise / this.totalPassages : 0
    },
    totalNouveauxClients() {
      return this.nouveauxClients.reduce((s, j) => s + parseInt(j.nb_nouveaux || 0), 0)
    },
    maxJour() {
      return Math.max(...this.parJourFiltre.map(j => parseFloat(j.ca_total || 0)), 1)
    },
    maxVille() {
      return Math.max(...this.parVille.map(v => parseFloat(v.ca_total || 0)), 1)
    },
    maxSemaine() {
      return Math.max(...this.parJourSemaine.map(j => parseFloat(j.ca_moyen || 0)), 1)
    },
    maxNouveaux() {
      return Math.max(...this.nouveauxClients.map(s => parseInt(s.nb_nouveaux || 0)), 1)
    },
    pctEspeces() {
      if (!this.paiements) return 50
      const total = parseFloat(this.paiements.total_especes || 0) + parseFloat(this.paiements.total_cb || 0)
      if (total === 0) return 50
      return Math.round(parseFloat(this.paiements.total_especes || 0) / total * 100)
    },
    courbeSemainePoints() {
      const n = this.parJourSemaine.length
      if (!n) return []
      return this.parJourSemaine.map((j, i) => ({
        x: n === 1 ? 365 : 44 + (i / (n - 1)) * 642,
        y: 140 - (this.maxSemaine > 0 ? (j.ca_moyen / this.maxSemaine * 110) : 0)
      }))
    },
    courbeSemaineLine() {
      const pts = this.courbeSemainePoints
      if (!pts.length) return ''
      return pts.map((p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`
        const prev = pts[i - 1]
        const cx = (prev.x + p.x) / 2
        return `C ${cx} ${prev.y} ${cx} ${p.y} ${p.x} ${p.y}`
      }).join(' ')
    },
    courbeSemaineArea() {
      const pts = this.courbeSemainePoints
      if (!pts.length) return ''
      return this.courbeSemaineLine + ` L ${pts[pts.length-1].x} 140 L ${pts[0].x} 140 Z`
    },
    courbePoints() {
      const n = this.parJourFiltre.length
      if (!n) return []
      return this.parJourFiltre.map((j, i) => ({
        x: n === 1 ? 365 : 44 + (i / (n - 1)) * 642,
        y: 140 - (this.maxJour > 0 ? (j.ca_total / this.maxJour * 110) : 0)
      }))
    },
    courbeLine() {
      const pts = this.courbePoints
      if (!pts.length) return ''
      return pts.map((p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`
        const prev = pts[i - 1]
        const cx = (prev.x + p.x) / 2
        return `C ${cx} ${prev.y} ${cx} ${p.y} ${p.x} ${p.y}`
      }).join(' ')
    },
    courbeArea() {
      const pts = this.courbePoints
      if (!pts.length) return ''
      return this.courbeLine + ` L ${pts[pts.length-1].x} 140 L ${pts[0].x} 140 Z`
    },
    moyenneAbsence() {
      if (!this.clientsInactifs.length) return 0
      const total = this.clientsInactifs.reduce((s, c) => s + Number(c.semaines_absence), 0)
      return Math.round(total / this.clientsInactifs.length)
    },
    pctCb() {
      return 100 - this.pctEspeces
    }
  },
  methods: {
    formatEuro(v) {
      return Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })
    },
    formatEuroShort(v) {
      const n = Number(v || 0)
      if (n >= 1000) return (n / 1000).toFixed(1) + 'k€'
      return n.toFixed(0) + '€'
    },
    formatJour(d) {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
    formatSemaine(d) {
      return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
    },
    nomJour(dow) {
      return ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dow] || '?'
    },
    setPeriode(p) {
      this.periode = p
    },
    courbeSemaineX(i) {
      const n = this.parJourSemaine.length
      return n === 1 ? 365 : 44 + (i / (n - 1)) * 642
    },
    courbeSemaineY(val) {
      return 140 - (this.maxSemaine > 0 ? (val / this.maxSemaine * 110) : 0)
    },
    setStyleSemaine(style) {
      this.styleGraphiqueSemaine = style
      localStorage.setItem('style_graphique_semaine', style)
    },
    courbeX(i) {
      const n = this.parJourFiltre.length
      return n === 1 ? 365 : 44 + (i / (n - 1)) * 642
    },
    courbeY(val) {
      return 140 - (this.maxJour > 0 ? (val / this.maxJour * 110) : 0)
    },
    setStyle(style) {
      this.styleGraphique = style
      localStorage.setItem('style_graphique', style)
    },
    sauvegarderSeuil() {
      localStorage.setItem('seuil_inactivite', String(this.seuilInactivite))
      this.chargerInactifs()
    },
    async chargerInactifs() {
      this.loadingInactifs = true
      this.seuilInactivite = parseInt(localStorage.getItem('seuil_inactivite') || '4')
      try {
        const res = await api.get('/clients/inactifs?semaines=' + this.seuilInactivite)
        this.clientsInactifs = res.data
      } catch (err) {
        console.error(err)
      } finally {
        this.loadingInactifs = false
      }
    },
    async charger() {
      this.loading = true
      try {
        const res = await api.get('/journee/stats-globales')
        this.parJour         = res.data.par_jour
        this.parVille        = res.data.par_ville
        this.parJourSemaine  = res.data.par_jour_semaine
        this.paiements       = res.data.paiements
        this.topClients      = res.data.top_clients
        this.nouveauxClients = res.data.nouveaux_clients
      } catch (err) {
        console.error(err)
      } finally {
        this.loading = false
      }
    }
  },
  mounted() { this.charger(); this.chargerInactifs() }
}
</script>

<style scoped>
*,
*::before,
*::after {
  box-sizing: border-box;
}

.page { padding: 20px; max-width: 1100px; margin: 0 auto; }
.page-title { font-size: 22px; font-weight: 700; margin-bottom: 20px; }
.card { background: white; border-radius: 14px; padding: 20px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); margin-bottom: 16px; }
.card h3 { font-size: 16px; font-weight: 700; margin-bottom: 16px; }
.loading-card { text-align: center; padding: 40px; color: #718096; }
.vide { text-align: center; color: #a0aec0; padding: 20px; font-size: 14px; }

/* Filtres période */
.filtres-card { padding: 12px 20px; }
.filtres-row { display: flex; gap: 8px; }
.periode-btn { padding: 8px 20px; border-radius: 20px; border: 1.5px solid #e2e8f0; background: white; font-size: 13px; font-weight: 600; cursor: pointer; color: #4a5568; transition: all 0.2s; }
.periode-btn:hover { border-color: #2d6a4f; color: #2d6a4f; }
.periode-btn.active { background: #2d6a4f; color: white; border-color: #2d6a4f; }

/* KPIs */
.kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.kpi-card { text-align: center; padding: 16px 8px; margin-bottom: 0; }
.kpi-icon { font-size: 22px; margin-bottom: 6px; }
.kpi-value { font-size: 18px; font-weight: 700; color: #2d3748; }
.kpi-label { font-size: 11px; color: #718096; margin-top: 2px; }

/* Graphique barres jour */
.chart-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.chart-header h3 { margin-bottom: 0; }
.chart-style-toggle { display: flex; gap: 4px; background: #f0f0f0; border-radius: 8px; padding: 3px; }
.chart-style-toggle button { background: none; border: none; padding: 5px 10px; border-radius: 6px; font-size: 14px; cursor: pointer; color: #718096; font-weight: 600; transition: all 0.2s; }
.chart-style-toggle button.active { background: white; color: #2d6a4f; box-shadow: 0 1px 4px rgba(0,0,0,0.1); }

.courbe-chart { width: 100%; }
.courbe-svg { width: 100%; height: 210px; display: block; }

.chart-wrap { overflow-x: auto; }
.barres-chart { display: flex; align-items: flex-end; gap: 6px; height: 140px; padding-bottom: 28px; min-width: 100%; }
.barre-item { flex: 1; min-width: 28px; max-width: 60px; display: flex; flex-direction: column; align-items: center; height: 100%; }
.barre-top-label { font-size: 9px; color: #718096; margin-bottom: 2px; height: 12px; white-space: nowrap; }
.barre-outer { flex: 1; width: 100%; display: flex; align-items: flex-end; background: #f0f0f0; border-radius: 4px 4px 0 0; overflow: hidden; }
.barre-inner { width: 100%; border-radius: 4px 4px 0 0; transition: height 0.4s; }
.b-ca { background: linear-gradient(180deg, #2d6a4f, #40916c); }
.b-clients { background: linear-gradient(180deg, #3182ce, #63b3ed); }
.barre-bot-label { font-size: 9px; color: #718096; margin-top: 4px; white-space: nowrap; }

/* CA par ville */
.marche-stats { display: flex; flex-direction: column; gap: 12px; }
.marche-row { display: flex; align-items: center; gap: 12px; }
.marche-info { min-width: 180px; }
.marche-nom { display: block; font-size: 14px; font-weight: 700; color: #2d3748; }
.marche-detail { font-size: 11px; color: #a0aec0; }
.marche-bar-wrap { flex: 1; height: 10px; background: #e2e8f0; border-radius: 5px; overflow: hidden; }
.marche-bar { height: 100%; background: linear-gradient(90deg, #2d6a4f, #40916c); border-radius: 5px; transition: width 0.5s; }
.marche-ca { min-width: 80px; text-align: right; font-size: 14px; font-weight: 700; color: #2d6a4f; }

/* Jour de semaine */
.semaine-grid { display: flex; gap: 8px; align-items: flex-end; height: 160px; padding-bottom: 0; }
.semaine-item { flex: 1; display: flex; flex-direction: column; align-items: center; height: 100%; }
.semaine-ca { font-size: 11px; color: #2d3748; font-weight: 700; margin-bottom: 4px; flex-shrink: 0; }
.semaine-bar-wrap { flex: 1; width: 80%; display: flex; align-items: flex-end; background: #f5f5f5; border-radius: 6px 6px 0 0; overflow: hidden; min-height: 8px; }
.semaine-bar { width: 100%; background: linear-gradient(180deg, #ed8936, #f6ad55); border-radius: 6px 6px 0 0; transition: height 0.6s cubic-bezier(0.4,0,0.2,1); min-height: 4px; }
.semaine-peak .semaine-bar { background: linear-gradient(180deg, #c05621, #ed8936); }
.semaine-peak .semaine-ca { color: #c05621; }
.semaine-jour { font-size: 11px; font-weight: 600; color: #718096; margin-top: 6px; flex-shrink: 0; }
.semaine-peak .semaine-jour { color: #c05621; font-weight: 700; }
.semaine-nb { font-size: 10px; color: #a0aec0; }

/* Paiements */
.paiement-row { display: flex; align-items: center; gap: 20px; }
.paiement-item { text-align: center; min-width: 100px; }
.paiement-icon { font-size: 28px; margin-bottom: 4px; }
.paiement-val { font-size: 18px; font-weight: 700; color: #2d3748; }
.paiement-label { font-size: 12px; color: #718096; }
.paiement-pct { font-size: 20px; font-weight: 700; color: #2d6a4f; margin-top: 4px; }
.paiement-divider { flex: 1; }
.paiement-barre { display: flex; height: 20px; border-radius: 10px; overflow: hidden; }
.paiement-barre-especes { background: #2d6a4f; transition: width 0.5s; }
.paiement-barre-cb { background: #3182ce; transition: width 0.5s; }

/* Top clients */
.table-top { width: 100%; border-collapse: collapse; font-size: 14px; }
.table-top th { text-align: left; padding: 8px 12px; font-size: 12px; color: #718096; border-bottom: 2px solid #e2e8f0; }
.table-top td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; }
.table-top .top-1 td { background: #fffbeb; }
.table-top .top-2 td { background: #f7fafc; }
.table-top .top-3 td { background: #fff5f5; }
.rank { font-size: 18px; }
.mono { font-family: monospace; font-size: 12px; color: #718096; }
.ca { font-weight: 700; color: #2d6a4f; }
.badge-pts { padding: 3px 10px; background: #f0fff4; color: #22543d; border-radius: 20px; font-size: 12px; font-weight: 600; }


/* Clients inactifs */
.inactifs-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; flex-wrap: wrap; gap: 10px; }
.inactifs-header h3 { margin-bottom: 0; }
.inactifs-meta { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.inactifs-seuil { font-size: 12px; color: #718096; background: #f7fafc; padding: 4px 10px; border-radius: 20px; }
.btn-rafraichir { background: #edf2f7; border: none; padding: 6px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; color: #4a5568; }
.btn-rafraichir:hover { background: #e2e8f0; }
.inactifs-kpis { display: flex; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.inactif-kpi { flex: 1; min-width: 100px; text-align: center; padding: 12px; background: #f7fafc; border-radius: 10px; }
.inactif-kpi strong { display: block; font-size: 22px; font-weight: 700; color: #2d3748; }
.inactif-kpi span { font-size: 11px; color: #718096; }
.inactif-kpi.warn strong { color: #e53e3e; }
.inactif-kpi.warn { background: #fff5f5; }
.inactifs-vide { color: #2d6a4f; font-weight: 600; }
.inactifs-seuil-edit { display: flex; align-items: center; gap: 6px; background: #f7fafc; padding: 4px 10px; border-radius: 20px; }
.seuil-input { width: 44px; border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 2px 6px; font-size: 13px; font-weight: 700; color: #2d6a4f; text-align: center; outline: none; }
.seuil-input:focus { border-color: #2d6a4f; }
.inactifs-list { display: flex; flex-direction: column; gap: 8px; }
.inactif-item { display: flex; align-items: center; gap: 12px; padding: 12px 14px; background: #f7fafc; border-radius: 10px; border-left: 3px solid #e2e8f0; }
.inactif-critique { background: #fff5f5; border-left-color: #e53e3e; }
.inactif-avatar { width: 38px; height: 38px; border-radius: 50%; background: #cbd5e0; color: #4a5568; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; text-transform: uppercase; }
.inactif-critique .inactif-avatar { background: #fed7d7; color: #c53030; }
.inactif-info { flex: 1; }
.inactif-info strong { display: block; font-size: 14px; color: #2d3748; }
.inactif-info span { font-size: 12px; color: #718096; }
.inactif-absence { text-align: center; min-width: 52px; }
.inactif-absence strong { display: block; font-size: 16px; font-weight: 700; color: #e53e3e; }
.inactif-absence span { font-size: 10px; color: #a0aec0; }
.badge-pts { background: #ebf8ff; color: #2b6cb0; border-radius: 20px; padding: 3px 10px; font-size: 12px; font-weight: 600; }

@media (max-width: 900px) { .kpis { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 600px) {
  .page { padding: 12px; }
  .kpis { grid-template-columns: repeat(2, 1fr); }
  .paiement-row { flex-direction: column; }
  .marche-info { min-width: 120px; }
}

/* Premium SaaS refresh */
.page {
  background: #F8FAFC;
  color: #0F172A;
  max-width: 1280px;
  min-height: calc(100vh - 64px);
  padding: 28px;
}

.stats-hero {
  align-items: flex-end;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 22px;
}

.eyebrow {
  color: #1E6FDC;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.page-title {
  color: #0D1B3D;
  font-size: 34px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.1;
  margin: 0;
}

.page-subtitle {
  color: #64748B;
  font-size: 14px;
  margin: 9px 0 0;
  max-width: 620px;
}

.card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  box-shadow: 0 18px 45px rgba(13, 27, 61, 0.06);
  margin-bottom: 18px;
  max-width: 100%;
  overflow: hidden;
  padding: 22px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.card:hover {
  border-color: rgba(30, 111, 220, 0.22);
  box-shadow: 0 22px 55px rgba(13, 27, 61, 0.08);
  transform: translateY(-1px);
}

.card h3 {
  color: #0D1B3D;
  font-size: 17px;
  font-weight: 800;
}

.filtres-card {
  align-items: center;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(13, 27, 61, 0.06);
  display: flex;
  gap: 14px;
  padding: 10px;
}

.filtres-label {
  color: #64748B;
  font-size: 12px;
  font-weight: 800;
  padding-left: 8px;
}

.filtres-row {
  gap: 6px;
}

.periode-btn {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  color: #64748B;
  font-size: 13px;
  font-weight: 800;
  padding: 10px 16px;
  transition: all 0.2s ease;
}

.periode-btn:hover {
  border-color: #1E6FDC;
  color: #1E6FDC;
}

.periode-btn.active {
  background: #0D1B3D;
  border-color: #0D1B3D;
  box-shadow: 0 10px 24px rgba(13, 27, 61, 0.18);
  color: #FFFFFF;
}

.loading-card,
.vide {
  align-items: center;
  color: #64748B;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 150px;
  padding: 34px;
}

.loading-card strong {
  color: #0D1B3D;
  font-size: 16px;
}

.loading-pulse {
  animation: pulse 1s ease-in-out infinite alternate;
  background: linear-gradient(135deg, #1E6FDC, #22C55E);
  border-radius: 999px;
  height: 12px;
  width: 72px;
}

@keyframes pulse {
  from { opacity: 0.45; transform: scaleX(0.75); }
  to { opacity: 1; transform: scaleX(1); }
}

.kpis {
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.kpi-card {
  min-height: 132px;
  padding: 18px;
  text-align: left;
}

.kpi-primary {
  background: linear-gradient(135deg, #0D1B3D 0%, #17366E 100%);
  border-color: #0D1B3D;
}

.kpi-icon {
  align-items: center;
  background: rgba(30, 111, 220, 0.1);
  border: 1px solid rgba(30, 111, 220, 0.18);
  border-radius: 12px;
  color: #1E6FDC;
  display: flex;
  font-size: 13px;
  font-weight: 900;
  height: 34px;
  justify-content: center;
  margin-bottom: 16px;
  width: 34px;
}

.kpi-icon.success {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.2);
  color: #22C55E;
}

.kpi-primary .kpi-icon {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.22);
  color: #FFFFFF;
}

.kpi-value {
  color: #0F172A;
  font-size: 23px;
  font-weight: 900;
  line-height: 1.1;
}

.kpi-label {
  color: #64748B;
  font-size: 12px;
  font-weight: 800;
  margin-top: 8px;
}

.kpi-primary .kpi-value,
.kpi-primary .kpi-label {
  color: #FFFFFF;
}

.chart-header {
  gap: 14px;
  margin-bottom: 16px;
}

.chart-style-toggle {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 4px;
}

.chart-style-toggle button {
  border-radius: 10px;
  color: #64748B;
  font-weight: 900;
  min-width: 36px;
  padding: 8px 10px;
}

.chart-style-toggle button.active {
  background: #FFFFFF;
  box-shadow: 0 8px 20px rgba(13, 27, 61, 0.08);
  color: #1E6FDC;
}

.chart-wrap {
  max-width: 100%;
  overflow-x: auto;
  padding: 4px 2px 0;
  -webkit-overflow-scrolling: touch;
}

.barres-chart {
  gap: 8px;
  height: 230px;
  min-width: 680px;
  padding: 10px 0 34px;
}

.barre-item {
  min-width: 34px;
}

.barre-top-label {
  color: #0D1B3D;
  font-size: 10px;
  font-weight: 800;
  height: 18px;
  margin-bottom: 6px;
}

.barre-outer {
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 999px 999px 10px 10px;
}

.barre-inner {
  border-radius: 999px 999px 10px 10px;
  transition: height 0.4s ease;
}

.b-ca {
  background: linear-gradient(180deg, #1E6FDC, #5EA3FF);
}

.b-clients {
  background: linear-gradient(180deg, #22C55E, #86EFAC);
}

.barre-bot-label {
  color: #64748B;
  font-size: 10px;
  font-weight: 700;
  margin-top: 8px;
}

.courbe-svg {
  height: 250px;
  min-width: 560px;
}

.chart-card .courbe-svg path[stroke],
.chart-card .courbe-svg circle {
  stroke: #1E6FDC;
}

.chart-card .courbe-svg text {
  fill: #64748B;
}

.marche-row {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  display: grid;
  grid-template-columns: minmax(145px, 0.8fr) 1.4fr auto;
  max-width: 100%;
  padding: 14px;
}

.marche-nom {
  color: #0F172A;
  font-weight: 900;
}

.marche-detail {
  color: #64748B;
  font-weight: 600;
}

.marche-bar-wrap {
  background: #E2E8F0;
  border-radius: 999px;
}

.marche-bar {
  background: linear-gradient(90deg, #1E6FDC, #22C55E);
  border-radius: 999px;
}

.marche-ca {
  color: #0D1B3D;
  font-weight: 900;
}

.semaine-scroll {
  max-width: 100%;
  overflow-x: auto;
  padding: 4px 2px 0;
  -webkit-overflow-scrolling: touch;
}

.semaine-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(48px, 1fr));
  gap: 10px;
  height: 220px;
  min-width: 0;
}

.semaine-item {
  min-width: 0;
}

.semaine-bar-wrap {
  max-width: 54px;
  width: min(80%, 54px);
}

.semaine-ca {
  color: #0D1B3D;
  font-weight: 900;
}

.semaine-bar-wrap {
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 999px 999px 10px 10px;
}

.semaine-bar {
  background: linear-gradient(180deg, #1E6FDC, #8EC5FF);
  border-radius: 999px 999px 10px 10px;
}

.semaine-peak .semaine-bar {
  background: linear-gradient(180deg, #22C55E, #86EFAC);
}

.semaine-peak .semaine-ca,
.semaine-peak .semaine-jour {
  color: #15803D;
}

.semaine-jour {
  color: #0F172A;
  font-weight: 900;
}

.semaine-nb {
  color: #64748B;
  font-weight: 600;
}

.paiement-row {
  display: grid;
  grid-template-columns: minmax(120px, 0.6fr) 1.4fr minmax(120px, 0.6fr);
}

.paiement-item {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 16px;
}

.paiement-icon {
  align-items: center;
  background: rgba(30, 111, 220, 0.1);
  border: 1px solid rgba(30, 111, 220, 0.18);
  border-radius: 12px;
  color: #1E6FDC;
  display: flex;
  font-size: 13px;
  font-weight: 900;
  height: 34px;
  justify-content: center;
  margin: 0 auto 10px;
  width: 34px;
}

.paiement-val {
  color: #0F172A;
  font-weight: 900;
}

.paiement-label {
  color: #64748B;
  font-weight: 700;
}

.paiement-pct {
  color: #1E6FDC;
  font-weight: 900;
}

.paiement-barre {
  background: #E2E8F0;
  border-radius: 999px;
  height: 24px;
}

.paiement-barre-especes {
  background: #22C55E;
}

.paiement-barre-cb {
  background: #1E6FDC;
}

.table-top {
  border-collapse: separate;
  border-spacing: 0 8px;
  min-width: 760px;
}

.table-scroll {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-top th {
  border-bottom: 0;
  color: #64748B;
  font-size: 11px;
  font-weight: 900;
  padding: 0 14px 6px;
  text-transform: uppercase;
}

.table-top td {
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  border-top: 1px solid #E2E8F0;
  color: #0F172A;
  padding: 14px;
}

.table-top td:first-child {
  border-left: 1px solid #E2E8F0;
  border-radius: 14px 0 0 14px;
}

.table-top td:last-child {
  border-radius: 0 14px 14px 0;
  border-right: 1px solid #E2E8F0;
}

.table-top .top-1 td {
  background: #F0F7FF;
}

.table-top .top-2 td,
.table-top .top-3 td {
  background: #FFFFFF;
}

.rank {
  color: #1E6FDC;
  font-weight: 900;
}

.ca {
  color: #0D1B3D;
  font-weight: 900;
}

.badge-pts {
  background: rgba(30, 111, 220, 0.1);
  border: 1px solid rgba(30, 111, 220, 0.16);
  color: #1E6FDC;
  font-weight: 800;
}

.inactifs-header {
  align-items: flex-start;
}

.inactifs-seuil-edit {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 8px 10px;
}

.inactifs-seuil-edit label,
.inactifs-seuil-edit span {
  color: #64748B;
  font-size: 12px;
  font-weight: 700;
}

.seuil-input {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  color: #0D1B3D;
  padding: 6px;
  width: 50px;
}

.seuil-input:focus {
  border-color: #1E6FDC;
  box-shadow: 0 0 0 3px rgba(30, 111, 220, 0.12);
}

.btn-rafraichir {
  background: #0D1B3D;
  border: 1px solid #0D1B3D;
  border-radius: 14px;
  color: #FFFFFF;
  padding: 10px 14px;
}

.btn-rafraichir:hover {
  background: #1E6FDC;
  border-color: #1E6FDC;
}

.inactif-kpi,
.inactif-item {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
}

.inactif-kpi strong,
.inactif-info strong {
  color: #0D1B3D;
  font-weight: 900;
}

.inactif-kpi span,
.inactif-info span,
.inactif-absence span {
  color: #64748B;
}

.inactif-kpi.warn,
.inactif-critique {
  background: #FFF7ED;
  border-color: #FED7AA;
}

.inactif-kpi.warn strong,
.inactif-absence strong {
  color: #C2410C;
}

.inactifs-vide {
  color: #15803D;
}

.inactif-avatar {
  background: #E0F2FE;
  border-radius: 14px;
  color: #1E6FDC;
}

.inactif-critique .inactif-avatar {
  background: #FFEDD5;
  color: #C2410C;
}

@media (max-width: 1100px) {
  .kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .page { padding: 20px; }
  .stats-hero { align-items: stretch; flex-direction: column; }
  .paiement-row { grid-template-columns: 1fr; }
}

@media (max-width: 640px) {
  .page { padding: 14px 14px 96px; }
  .page-title { font-size: 28px; }
  .filtres-card { align-items: stretch; flex-direction: column; }
  .filtres-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .periode-btn { padding: 10px 8px; }
  .kpis {
    display: flex;
    gap: 12px;
    margin-left: -14px;
    margin-right: -14px;
    overflow-x: auto;
    padding: 0 14px 4px;
  }
  .kpi-card { flex: 0 0 220px; }
  .card { padding: 18px; }
  .chart-header,
  .inactifs-header { align-items: stretch; flex-direction: column; }
  .barres-chart { min-width: 620px; }
  .semaine-grid { min-width: 560px; }
  .marche-row { grid-template-columns: 1fr; }
  .marche-ca { text-align: left; }
  .table-top,
  .table-top tbody,
  .table-top tr,
  .table-top td {
    display: block;
    min-width: 0;
    width: 100%;
  }
  .table-top {
    border-spacing: 0;
    min-width: 0;
  }
  .table-top thead { display: none; }
  .table-top tr {
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    margin-bottom: 10px;
    padding: 12px;
  }
  .table-top td,
  .table-top td:first-child,
  .table-top td:last-child {
    background: transparent;
    border: 0;
    border-radius: 0;
    padding: 6px 0;
  }
  .table-top td:not(.rank) {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
  }
  .table-top td:not(.rank)::before {
    color: #64748B;
    content: attr(data-label);
    font-size: 11px;
    font-weight: 900;
    text-transform: uppercase;
  }
  .inactifs-kpis { grid-template-columns: 1fr; }
  .inactif-item {
    align-items: flex-start;
    flex-direction: column;
  }
  .inactif-absence { text-align: left; }
}
</style>
